import { Injectable } from '@angular/core'
import { Observable, concatMap, from, last, scan } from 'rxjs'
import { ManufacturersService } from './manufacturers.service'
import { Page } from '../../models/util.model'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  ManufacturerModalCode,
  ManufacturerNotificationCode,
  parseManufacturerModal,
  parseManufacturerNotification,
} from './libs/manufacturer-notification.lib'
import { ManufacturerSearchParams } from './models/manufacturer-search.model'
import { Manufacturer } from './models/manufacturer.entity'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class ManufacturersRepository {
  constructor(
    private manufacturersService: ManufacturersService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search manufacturers and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search manufacturers
   */
  searchManufacturers$(
    searchParams: ManufacturerSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Manufacturer>> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.manufacturersService.limit

    return needsPagination
      ? this._paginateManufacturers$(searchParams)
      : this._searchManufacturers$(searchParams, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the manufacturers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Manufacturer>> for search manufacturers
   */
  private _searchManufacturers$(
    searchParams: ManufacturerSearchParams,
    returnAll?: boolean,
  ): Observable<Page<Manufacturer>> {
    return this.manufacturersService.search$(searchParams, returnAll).pipe()
  }

  /**
   * Search more than 200 manufacturers with relative external data
   * @param searchParams - the search params
   * @param fields - the manufacturers fields
   * @param listingData - the already loaded listing data
   * @returns the observable<Page<Manufacturer>> for search manufacturers
   */
  private _paginateManufacturers$(
    searchParams: ManufacturerSearchParams,
  ): Observable<Page<Manufacturer>> {
    const totalCount = searchParams.limit || this.manufacturersService.limit
    const pagesCount = Math.ceil(totalCount / this.manufacturersService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.manufacturersService.limit,
        offset: i * this.manufacturersService.limit,
      })),
    ).pipe(
      concatMap((pageParams) => this._searchManufacturers$(pageParams)),
      scan<Page<Manufacturer>, Page<Manufacturer>>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
        }),
        { totalCount: 0, data: [], extData: {} } as Page<Manufacturer>,
      ),
      last(),
    )
  }

  notify(options: {
    code?: ManufacturerNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseManufacturerNotification(options.code),
    )
  }

  async alert(
    code: ManufacturerModalCode,
    manufacturer?: Manufacturer,
  ): Promise<ModalResponse> {
    const settings = parseManufacturerModal(code, manufacturer)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
